import {
  AI_TICKET_DEFLECTION_ROUTE,
  VOICE_AI_PAGE_ROUTE,
} from '@constants/routes';
import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import {
  STUDIO_TYPE_LABELS,
  StudioType,
} from '@conversed/shared/src/central/types/studio.type';
import { useModal } from '@hooks/modal/useModal';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { usePermissions } from '@hooks/usePermissions';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export const useStudioInstallAction = () => {
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { hasPermissions } = usePermissions();
  const { selectedCompany } = useCompanySelector();
  const { t } = useTranslation();
  const companyName = selectedCompany?.name ?? '';

  const openAccessRequestEmail = useCallback(
    (studioLabel: string) => {
      window.location.href = `mailto:sales@conversed.ai?subject=${encodeURIComponent(
        `${companyName} - ${studioLabel} - Access Request`,
      )}`;
    },
    [companyName],
  );

  const getStudioAction = useCallback(
    (type: StudioType): (() => void) => {
      switch (type) {
        case StudioType.TICKET_AI:
          return () => {
            if (
              hasPermissions([
                PERMISSIONS.TICKET_DEFLECTION.TICKET_DEFLECTION_SUBMIT,
                PERMISSIONS.TICKET_DEFLECTION.TICKET_DEFLECTION_FEEDBACK,
              ])
            ) {
              navigate(AI_TICKET_DEFLECTION_ROUTE);
            } else {
              openAccessRequestEmail(STUDIO_TYPE_LABELS[StudioType.TICKET_AI]);
            }
          };
        case StudioType.EMAIL_AI:
          return () =>
            openAccessRequestEmail(STUDIO_TYPE_LABELS[StudioType.EMAIL_AI]);
        case StudioType.VOICE_AI:
          return () => {
            if (
              hasPermissions([PERMISSIONS.VOICE_AI.VOICE_AI_CONFIGURATION_FORM])
            ) {
              showModal(
                'general-prompt-modal-v2',
                t('voice-ai.request-access.title'),
                {
                  type: 'confirm',
                  description: t('voice-ai.request-access.desc'),
                  onSubmit: () => navigate(VOICE_AI_PAGE_ROUTE),
                  modalSize: 'md',
                },
              );
            } else {
              openAccessRequestEmail(STUDIO_TYPE_LABELS[StudioType.VOICE_AI]);
            }
          };
        default:
          return () =>
            openAccessRequestEmail(STUDIO_TYPE_LABELS[StudioType.CHAT_AI]);
      }
    },
    [navigate, showModal, hasPermissions, t, openAccessRequestEmail],
  );

  return { getStudioAction };
};
